import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Pencil, Play, Image, ExternalLink, ChevronUp, ChevronDown, Eye, EyeOff } from 'lucide-react';
import api, { isAuthenticated } from '../utils/api';
import { toast } from 'sonner';

/*
  Bento grid positions:
  ┌─────────────┬─────────────┬─────────────┐
  │ slot0       │ slot1       │ slot2       │  Row 1
  ├─────────────┼─────────────┼─────────────┤
  │ slot3       │ slot4       │ slot5       │  Row 2
  ├─────────────────────────────────────────┤
  │             slot6: SAREES               │  Row 3
  └─────────────────────────────────────────┘
*/

const SLOTS = [
  { id: 'slot0', label: '① LEHENGAS',         hint: 'Row 1 left — portrait',        color: '#7c3aed', defaultLabel: 'LEHENGAS' },
  { id: 'slot1', label: '② CORDS',            hint: 'Row 1 center — portrait',      color: '#0369a1', defaultLabel: 'CORDS' },
  { id: 'slot2', label: '③ GOWNS',            hint: 'Row 1 right — portrait',       color: '#047857', defaultLabel: 'GOWNS' },
  { id: 'slot3', label: '④ BRIDAL LEHENGAS',  hint: 'Row 2 left — portrait',        color: '#be185d', defaultLabel: 'BRIDAL LEHENGAS' },
  { id: 'slot4', label: '⑤ CHUDIDHAR',        hint: 'Row 2 center — portrait',      color: '#b45309', defaultLabel: 'CHUDIDHAR' },
  { id: 'slot5', label: '⑥ UNSTICHED SHOOTS', hint: 'Row 2 right — portrait',       color: '#0891b2', defaultLabel: 'UNSTICHED SHOOTS' },
  { id: 'slot6', label: '⑦ SAREES',           hint: 'Row 3 full width — landscape', color: '#dc2626', defaultLabel: 'SAREES' },
];

const getBentoLink = (label) => {
  const map = {
    'LEHENGAS':         '/collections/lehengas',
    'SAREES':           '/collections/sarees',
    'GOWNS':            '/collections/gowns',
    'CORDS':            '/collections/cord-sets',
    'CORD SETS':        '/collections/cord-sets',
    'CHUDIDHAR':        '/collections/chudidhar',
    'INDO-WESTERN':     '/collections/indo-western',
    'BRIDAL LEHENGAS':  '/collections/bridal-lehenga',
    'BRIDAL LEHENGA':   '/collections/bridal-lehenga',
    'UNSTICHED SHOOTS': '/collections/unstitched-suits',
    'UNSTICHED':        '/collections/unstitched-suits',
    'UNSTITCHED':       '/collections/unstitched-suits',
  };
  const key = label.trim().toUpperCase();
  return map[key] || `/collections/${label.trim().toLowerCase().replace(/\s+/g, '-')}`;
};

// ── YouTube helpers ──────────────────────────────────────────────────────────
function extractYouTubeId(url) {
  if (!url) return null;
  const short = url.match(/youtu\.be\/([^?&]+)/);
  if (short) return short[1];
  const long = url.match(/(?:v=|\/embed\/|\/shorts\/)([^?&/]+)/);
  if (long) return long[1];
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;
  return null;
}
function getYouTubeThumbnail(videoId) {
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
}
function buildEmbedUrl(videoId) {
  if (!videoId) return '';
  const params = new URLSearchParams({
    autoplay: '1', mute: '1', loop: '1', playlist: videoId,
    controls: '0', modestbranding: '1', rel: '0', playsinline: '1',
  });
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params}`;
}

// ── Hero Slide helpers ───────────────────────────────────────────────────────
const EMPTY_SLIDE = {
  image: '', headline: '', subheading: '',
  cta1_text: '', cta1_link: '',
  cta2_text: '', cta2_link: '',
};

// ── Reel helpers ─────────────────────────────────────────────────────────────
const EMPTY_REEL = { youtube_url: '', caption: '' };

// ── Hero Slide Modal ─────────────────────────────────────────────────────────
const HeroSlideModal = ({ mode, slide, onClose, onSaved }) => {
  const [form, setForm] = useState(slide ? {
    image:     slide.image     || '',
    headline:  slide.headline  || '',
    subheading:slide.subheading|| '',
    cta1_text: slide.cta1_text || '',
    cta1_link: slide.cta1_link || '',
    cta2_text: slide.cta2_text || '',
    cta2_link: slide.cta2_link || '',
  } : { ...EMPTY_SLIDE });
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.image.trim())    { toast.error('Image URL is required'); return; }
    if (!form.headline.trim()) { toast.error('Headline is required'); return; }
    if (!form.cta1_text.trim() || !form.cta1_link.trim()) {
      toast.error('CTA 1 text and link are required'); return;
    }
    setSaving(true);
    try {
      if (mode === 'edit') {
        // Delete old + recreate (API has no PUT for hero slides by order)
        await api.delete(`/hero-slides/${slide.order}`);
      }
      await api.post('/hero-slides', {
        image:      form.image.trim(),
        headline:   form.headline.trim(),
        subheading: form.subheading.trim(),
        cta1_text:  form.cta1_text.trim(),
        cta1_link:  form.cta1_link.trim(),
        cta2_text:  form.cta2_text.trim(),
        cta2_link:  form.cta2_link.trim(),
      });
      toast.success(mode === 'edit' ? 'Slide updated!' : 'Slide added!');
      onSaved();
    } catch (e) {
      toast.error('Error saving slide');
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0" style={{ background: '#2A0000' }}>
          <div>
            <h3 className="font-bold text-white text-sm">
              {mode === 'add' ? '＋ Add Hero Slide' : '✎ Edit Hero Slide'}
            </h3>
            <p className="text-white/60 text-xs mt-0.5">This slide appears in the homepage banner carousel</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-lg leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">

          {/* Image URL */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Background Image URL <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.image}
              onChange={e => { set('image', e.target.value); setImgError(false); }}
              placeholder="https://images.unsplash.com/..."
              className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30 font-mono"
              autoFocus
            />
            {form.image && (
              <div className="mt-2 relative rounded-xl overflow-hidden border bg-gray-100" style={{ aspectRatio: '16/6' }}>
                {!imgError ? (
                  <>
                    <img
                      src={form.image}
                      alt="preview"
                      className="w-full h-full object-cover"
                      onError={() => setImgError(true)}
                    />
                    {/* Overlay simulation */}
                    <div className="absolute inset-0 bg-black/30 flex items-end p-4">
                      <div className="text-white">
                        <p className="font-bold text-base leading-tight drop-shadow">
                          {form.headline || 'Your headline here'}
                        </p>
                        <p className="text-xs opacity-80 mt-0.5">
                          {form.subheading || 'Your subheading here'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {form.cta1_text && (
                            <span className="text-[10px] bg-[#E5A93C] text-[#2A0000] font-bold px-2 py-0.5 rounded">
                              {form.cta1_text}
                            </span>
                          )}
                          {form.cta2_text && (
                            <span className="text-[10px] border border-white text-white px-2 py-0.5 rounded">
                              {form.cta2_text}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="absolute top-2 right-2 text-[9px] bg-black/60 text-white px-2 py-0.5 rounded-full">Live Preview</span>
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                    ⚠ Could not load image — check URL
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Headline + Subheading */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Headline <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.headline}
                onChange={e => set('headline', e.target.value)}
                placeholder="e.g. Premium Ethnic Wear Collection"
                maxLength={80}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Subheading</label>
              <input
                type="text"
                value={form.subheading}
                onChange={e => set('subheading', e.target.value)}
                placeholder="e.g. Discover timeless elegance in every thread"
                maxLength={120}
                className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
              />
            </div>
          </div>

          {/* CTA 1 */}
          <div>
            <p className="text-xs font-bold text-gray-700 mb-2">
              CTA Button 1 <span className="text-red-500">*</span>
              <span className="ml-1 text-[10px] font-normal text-gray-400">(Primary — gold button)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Button Text</label>
                <input
                  type="text"
                  value={form.cta1_text}
                  onChange={e => set('cta1_text', e.target.value)}
                  placeholder="e.g. Explore Collections"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Link / URL</label>
                <input
                  type="text"
                  value={form.cta1_link}
                  onChange={e => set('cta1_link', e.target.value)}
                  placeholder="/collections"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                />
              </div>
            </div>
          </div>

          {/* CTA 2 */}
          <div>
            <p className="text-xs font-bold text-gray-700 mb-2">
              CTA Button 2
              <span className="ml-1 text-[10px] font-normal text-gray-400">(Optional — outline button)</span>
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Button Text</label>
                <input
                  type="text"
                  value={form.cta2_text}
                  onChange={e => set('cta2_text', e.target.value)}
                  placeholder="e.g. WhatsApp Us"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                />
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Link / URL</label>
                <input
                  type="text"
                  value={form.cta2_link}
                  onChange={e => set('cta2_link', e.target.value)}
                  placeholder="https://wa.me/91..."
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-40 transition-opacity"
            style={{ background: '#2A0000' }}
          >
            {saving ? 'Saving…' : mode === 'add' ? 'Add Slide' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const AdminContentPage = () => {
  const [tab, setTab]         = useState('hero');
  const [heroSlides, setHeroSlides] = useState([]);
  const [rituals, setRituals] = useState([]);
  const [reels, setReels]     = useState([]);
  const [bento, setBento]     = useState({});
  const [showcase, setShowcase] = useState([]);

  // Hero modal
  const [heroModal, setHeroModal] = useState(null); // null | { mode:'add'|'edit', slide? }

  // Showcase modal
  const [scModal, setScModal] = useState(null);
  const [scForm,  setScForm]  = useState({ images: ['', '', '', ''] });

  // Reel modal
  const [reelModal, setReelModal] = useState(null);
  const [reelForm,  setReelForm]  = useState(EMPTY_REEL);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [h, r, e, b, s] = await Promise.all([
        api.get('/hero-slides'),
        api.get('/ritual-carousel'),
        api.get('/reels'),
        api.get('/bento-categories'),
        api.get('/customer-showcase'),
      ]);
      setHeroSlides(h.data);
      setRituals(r.data);
      setReels(e.data);
      const bentoMap = {};
      b.data.forEach(item => { bentoMap[item.grid_position] = item; });
      setBento(bentoMap);
      setShowcase(s.data);
    } catch (e) { console.error(e); }
  };

  // ── Hero Slides ──────────────────────────────────────────────────────────
  const deleteHeroSlide = async (order) => {
    if (!window.confirm('Delete this hero slide?')) return;
    try {
      await api.delete(`/hero-slides/${order}`);
      toast.success('Slide deleted.');
      fetchAll();
    } catch (e) { toast.error('Error deleting slide'); }
  };

  // ── Ritual ── 
  const addRitual = async () => {
    const label = prompt('Label:'); if (!label) return;
    const image = prompt('Image URL:'); if (!image) return;
    const link  = prompt('Link:') || '/collections';
    try { await api.post('/ritual-carousel', { label, image, link }); toast.success('Added!'); fetchAll(); }
    catch (e) { toast.error('Error'); }
  };
  const deleteRitual = async (order) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/ritual-carousel/${order}`); fetchAll(); } catch (e) { toast.error('Error'); }
  };

  // ── Reels ──
  const openAddReel  = () => { setReelForm(EMPTY_REEL); setReelModal({ mode: 'add' }); };
  const openEditReel = (reel) => {
    setReelForm({ youtube_url: reel.instagram_url || '', caption: reel.caption || '' });
    setReelModal({ mode: 'edit', reel });
  };
  const closeReelModal = () => setReelModal(null);
  const submitReelModal = async () => {
    const ytUrl = reelForm.youtube_url.trim();
    if (!ytUrl) { toast.error('YouTube URL is required'); return; }
    const videoId = extractYouTubeId(ytUrl);
    if (!videoId) { toast.error('Invalid YouTube URL'); return; }
    const autoThumb = getYouTubeThumbnail(videoId);
    try {
      if (reelModal.mode === 'edit') {
        await api.put(`/reels/${reelModal.reel.order}`, { thumbnail: autoThumb, instagram_url: ytUrl, caption: reelForm.caption.trim() });
        toast.success('Reel updated!');
      } else {
        await api.post('/reels', { thumbnail: autoThumb, instagram_url: ytUrl, caption: reelForm.caption.trim() });
        toast.success('Reel added!');
      }
      closeReelModal(); fetchAll();
    } catch (e) { toast.error('Error saving reel'); }
  };
  const deleteReel = async (order) => {
    if (!window.confirm('Delete this reel?')) return;
    try { await api.delete(`/reels/${order}`); fetchAll(); } catch (e) { toast.error('Error'); }
  };

  // ── Bento ──
  const addOrEditSlot = async (slot) => {
    const existing = bento[slot.id];
    const label = prompt(`${slot.label}\n${slot.hint}\n\nCategory label:`, existing?.label || slot.defaultLabel);
    if (!label) return;
    const image = prompt('Image URL:', existing?.image || '');
    if (!image) return;
    const autoLink = getBentoLink(label);
    const link = prompt('Collection link:', existing?.link || autoLink);
    if (link === null) return;
    if (existing) {
      try { await api.delete(`/bento-categories/${existing.order}`); }
      catch (e) { toast.error('Error replacing slot'); return; }
    }
    try {
      await api.post('/bento-categories', { label: label.toUpperCase().trim(), image, link, grid_position: slot.id });
      toast.success(`${slot.label} updated!`); fetchAll();
    } catch (e) { toast.error('Error saving'); }
  };
  const deleteSlot = async (slot) => {
    const item = bento[slot.id];
    if (!item) return;
    if (!window.confirm(`Remove ${item.label} from ${slot.label}?`)) return;
    try { await api.delete(`/bento-categories/${item.order}`); toast.success('Slot cleared.'); fetchAll(); }
    catch (e) { toast.error('Error'); }
  };

  // ── Customer Showcase ──
  const openAddModal  = () => { setScForm({ images: ['', '', '', ''] }); setScModal({ mode: 'add' }); };
  const openEditModal = (card) => {
    const imgs = Array.isArray(card.images) ? card.images : [card.image1 || '', card.image2 || ''];
    setScForm({ images: [...imgs, '', '', '', ''].slice(0, 4) });
    setScModal({ mode: 'edit', card });
  };
  const closeScModal  = () => setScModal(null);
  const submitScModal = async () => {
    const filled = scForm.images.map(s => s.trim()).filter(Boolean);
    if (filled.length === 0) { toast.error('At least one image URL is required'); return; }
    try {
      if (scModal.mode === 'edit') await api.delete(`/customer-showcase/${scModal.card.order}`);
      await api.post('/customer-showcase', { images: filled });
      toast.success(scModal.mode === 'edit' ? 'Card updated!' : 'Card added!');
      setScModal(null); fetchAll();
    } catch (e) { toast.error('Error saving card'); }
  };
  const deleteShowcaseCard = async (order) => {
    if (!window.confirm('Delete this card?')) return;
    try { await api.delete(`/customer-showcase/${order}`); toast.success('Deleted.'); fetchAll(); }
    catch (e) { toast.error('Error'); }
  };

  const previewVideoId = extractYouTubeId(reelForm.youtube_url);

  const TABS = [
    ['hero',     `Hero Slides (${heroSlides.length})`],
    ['ritual',   `Ritual Carousel (${rituals.length})`],
    ['reels',    `Reels (${reels.length})`],
    ['bento',    `Bento (${Object.keys(bento).length}/7)`],
    ['showcase', `Customer Showcase (${showcase.length})`],
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center space-x-4">
          <Link to="/admin/dashboard" className="text-[#E5A93C]"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="font-heading text-xl font-bold text-[#2A0000]">Homepage Content Manager</h1>
        </div>
      </header>

      <div className="p-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-lg p-1 border w-fit">
          {TABS.map(([key, lbl]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm rounded transition-colors whitespace-nowrap ${tab === key ? 'bg-[#2A0000] text-white' : 'hover:bg-gray-100'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {/* ══ HERO SLIDES TAB ══════════════════════════════════════════════════ */}
        {tab === 'hero' && (
          <div className="space-y-4">
            {/* Info bar */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-3">
              <span className="text-lg">🖼</span>
              <div className="text-xs text-amber-800 space-y-0.5">
                <p className="font-semibold">Hero Banner Carousel</p>
                <p>These slides appear at the top of the homepage. Slides auto-advance every 5 seconds. Recommended image ratio: <strong>16:9</strong> or <strong>wide landscape</strong> (min 1400px wide).</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-[#2A0000]">Hero Slides</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{heroSlides.length} slide{heroSlides.length !== 1 ? 's' : ''} active</p>
                </div>
                <button
                  onClick={() => setHeroModal({ mode: 'add' })}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg"
                  style={{ background: '#2A0000' }}
                >
                  <Plus className="w-4 h-4" /> Add Slide
                </button>
              </div>

              {heroSlides.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed rounded-xl text-gray-400 text-sm italic space-y-2">
                  <Image className="w-10 h-10 mx-auto opacity-30" />
                  <p>No hero slides yet — click "Add Slide" to create the first one.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {heroSlides.map((slide, idx) => (
                    <div key={slide.order ?? idx} className="border rounded-xl overflow-hidden bg-gray-50 flex flex-col md:flex-row gap-0">
                      {/* Thumbnail */}
                      <div className="relative md:w-64 shrink-0" style={{ aspectRatio: '16/7' }}>
                        <img
                          src={slide.image}
                          alt={slide.headline}
                          className="w-full h-full object-cover"
                          onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="256" height="112" fill="%23e5e7eb"><rect width="256" height="112"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">No image</text></svg>'; }}
                        />
                        {/* Slide number badge */}
                        <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-[#2A0000] text-white text-[10px] font-bold flex items-center justify-center shadow">
                          {idx + 1}
                        </span>
                        {/* Overlay preview */}
                        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex flex-col justify-end p-3">
                          <p className="text-white font-bold text-xs leading-tight drop-shadow line-clamp-2">{slide.headline}</p>
                          {slide.subheading && <p className="text-white/70 text-[9px] mt-0.5 line-clamp-1">{slide.subheading}</p>}
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                        <div className="space-y-2">
                          <div>
                            <p className="font-semibold text-[#2A0000] text-sm line-clamp-1">{slide.headline}</p>
                            {slide.subheading && (
                              <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{slide.subheading}</p>
                            )}
                          </div>

                          {/* CTA chips */}
                          <div className="flex flex-wrap gap-2">
                            {slide.cta1_text && (
                              <div className="flex items-center gap-1 bg-[#E5A93C]/15 border border-[#E5A93C]/40 text-[#2A0000] text-[10px] font-medium px-2 py-0.5 rounded-full">
                                <span className="text-[8px] font-bold text-[#E5A93C] uppercase">CTA1</span>
                                {slide.cta1_text}
                                <span className="text-gray-400 font-mono text-[8px]">→ {slide.cta1_link}</span>
                              </div>
                            )}
                            {slide.cta2_text && (
                              <div className="flex items-center gap-1 bg-gray-100 border border-gray-200 text-gray-600 text-[10px] font-medium px-2 py-0.5 rounded-full">
                                <span className="text-[8px] font-bold text-gray-400 uppercase">CTA2</span>
                                {slide.cta2_text}
                                <span className="text-gray-400 font-mono text-[8px]">→ {slide.cta2_link}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={() => setHeroModal({ mode: 'edit', slide })}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 font-medium transition-colors"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => deleteHeroSlide(slide.order)}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-red-300 text-red-500 hover:bg-red-50 font-medium transition-colors"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                          <span className="ml-auto text-[10px] text-gray-400 font-mono">order: {slide.order}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Backend note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-800 space-y-1">
              <p className="font-semibold">⚙ Backend note</p>
              <p>
                The current API has <code className="bg-white/60 px-1 rounded">POST /hero-slides</code> and{' '}
                <code className="bg-white/60 px-1 rounded">GET /hero-slides</code> but no <code className="bg-white/60 px-1 rounded">PUT</code> endpoint.
                Editing a slide deletes the old one and creates a new one — the order number will increment.
                Add a <code className="bg-white/60 px-1 rounded">DELETE /hero-slides/{'{order}'}</code> and{' '}
                <code className="bg-white/60 px-1 rounded">PUT /hero-slides/{'{order}'}</code> to your backend for full support
                (see the snippet below).
              </p>
            </div>
          </div>
        )}

        {/* ══ RITUAL TAB ══════════════════════════════════════════════════════ */}
        {tab === 'ritual' && (
          <div className="bg-white rounded-lg border p-6">
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-bold">Ritual Carousel Items</h2>
              <button onClick={addRitual} className="btn-primary flex items-center space-x-2 py-2 px-4"><Plus className="w-4 h-4" /><span>Add</span></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {rituals.map(item => (
                <div key={item.order} className="border rounded overflow-hidden">
                  <img src={item.image} alt={item.label} className="w-full h-48 object-cover" />
                  <div className="p-3">
                    <p className="font-medium text-sm">{item.label}</p>
                    <p className="text-xs text-gray-500 truncate">{item.link}</p>
                    <button onClick={() => deleteRitual(item.order)} className="text-red-500 mt-2"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ REELS TAB ═══════════════════════════════════════════════════════ */}
        {tab === 'reels' && (
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold">YouTube Reels</h2>
                <p className="text-xs text-gray-500 mt-1">Paste any YouTube URL — videos play continuously on the storefront.</p>
              </div>
              <button onClick={openAddReel} className="btn-primary flex items-center space-x-2 py-2 px-4 flex-shrink-0">
                <Plus className="w-4 h-4" /><span>Add Reel</span>
              </button>
            </div>
            {reels.length === 0 ? (
              <div className="text-center py-16 text-gray-400 text-sm italic border-2 border-dashed rounded-lg">
                No reels yet — click "Add Reel" to upload the first YouTube video.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {reels.map(reel => {
                  const vid = extractYouTubeId(reel.instagram_url);
                  const thumb = reel.thumbnail || getYouTubeThumbnail(vid);
                  return (
                    <div key={reel.order} className="border rounded-xl overflow-hidden">
                      <div className="relative aspect-[9/16] bg-gray-900 overflow-hidden">
                        {thumb ? (
                          <img src={thumb} alt={reel.caption || 'Reel'} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">No thumbnail</div>
                        )}
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                          </div>
                        </div>
                        <div className="absolute top-2 left-2 bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">YT</div>
                      </div>
                      <div className="p-3 space-y-1.5">
                        {reel.caption && <p className="text-sm font-medium truncate text-gray-800">{reel.caption}</p>}
                        <p className="text-[10px] text-gray-400 font-mono truncate">{reel.instagram_url}</p>
                        {vid
                          ? <p className="text-[10px] text-green-600 font-medium">✓ Video ID: {vid}</p>
                          : <p className="text-[10px] text-red-500 font-medium">⚠ Invalid YouTube URL</p>
                        }
                        <div className="flex gap-2 pt-1">
                          <button onClick={() => openEditReel(reel)} className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 font-medium">
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                          <button onClick={() => deleteReel(reel.order)} className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-red-300 text-red-500 hover:bg-red-50 font-medium">
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══ BENTO TAB ═══════════════════════════════════════════════════════ */}
        {tab === 'bento' && (
          <div className="bg-white rounded-lg border p-6">
            <div className="mb-5">
              <h2 className="text-lg font-bold">Bento "Shop by Categories"</h2>
              <p className="text-xs text-gray-500 mt-1">Each slot is independent — editing or deleting one never affects the others.</p>
            </div>
            <div className="bg-gray-50 border rounded-lg p-4 mb-6 select-none">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">Grid Position Map</p>
              <div style={{ display:'flex', gap:5, marginBottom:5 }}>
                {SLOTS.slice(0,3).map(slot => (
                  <div key={slot.id} style={{ flex:1, height:56, background: bento[slot.id] ? slot.color+'18' : '#f9fafb', border:`2px solid ${bento[slot.id] ? slot.color : '#e5e7eb'}`, borderRadius:6, padding:8, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                    <span style={{ fontSize:9, fontWeight:700, color: bento[slot.id] ? slot.color : '#9ca3af' }}>{slot.label}</span>
                    <span style={{ fontSize:8, color:'#9ca3af' }}>portrait</span>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:5, marginBottom:5 }}>
                {SLOTS.slice(3,6).map(slot => (
                  <div key={slot.id} style={{ flex:1, height:56, background: bento[slot.id] ? slot.color+'18' : '#f9fafb', border:`2px solid ${bento[slot.id] ? slot.color : '#e5e7eb'}`, borderRadius:6, padding:8, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                    <span style={{ fontSize:9, fontWeight:700, color: bento[slot.id] ? slot.color : '#9ca3af' }}>{slot.label}</span>
                    <span style={{ fontSize:8, color:'#9ca3af' }}>portrait</span>
                  </div>
                ))}
              </div>
              <div style={{ height:32, background: bento['slot6'] ? SLOTS[6].color+'18' : '#f9fafb', border:`2px solid ${bento['slot6'] ? SLOTS[6].color : '#e5e7eb'}`, borderRadius:6, padding:'0 12px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontSize:9, fontWeight:700, color: bento['slot6'] ? SLOTS[6].color : '#9ca3af' }}>{SLOTS[6].label} — full width landscape</span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SLOTS.map((slot) => {
                const item = bento[slot.id];
                return (
                  <div key={slot.id} className={`border-2 rounded-lg overflow-hidden ${slot.id === 'slot6' ? 'md:col-span-2' : ''}`} style={{ borderColor: item ? slot.color : '#e5e7eb' }}>
                    <div className="px-3 py-2 flex items-center justify-between" style={{ backgroundColor: item ? slot.color+'15' : '#f9fafb' }}>
                      <div>
                        <p className="text-xs font-bold" style={{ color: item ? slot.color : '#9ca3af' }}>{slot.label}</p>
                        <p className="text-[10px] text-gray-400">{slot.hint}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                        <button onClick={() => addOrEditSlot(slot)}
                          className="flex items-center gap-1 text-xs px-2 py-1 rounded border font-medium transition-colors"
                          style={{ borderColor:slot.color, color:slot.color, background:'white' }}>
                          {item ? <><Pencil className="w-3 h-3" /> Edit</> : <><Plus className="w-3 h-3" /> Add</>}
                        </button>
                        {item && (
                          <button onClick={() => deleteSlot(slot)} className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-300 text-red-500 hover:bg-red-50 font-medium transition-colors">
                            <Trash2 className="w-3 h-3" /> Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {item ? (
                      <div className="p-3 space-y-3">
                        <div className="flex gap-3 items-center">
                          <img src={item.image} alt={item.label} className="w-16 h-16 object-cover rounded flex-shrink-0" onError={e => { e.target.style.display='none'; }} />
                          <div className="min-w-0">
                            <p className="font-semibold text-sm">{item.label}</p>
                            <span className="inline-block mt-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Active</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-50 border rounded px-2 py-1.5">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide flex-shrink-0">Link</span>
                          <span className="text-xs text-gray-600 truncate flex-1 font-mono">{item.link}</span>
                          <button
                            onClick={async () => {
                              const newLink = prompt('Edit collection link:', item.link);
                              if (!newLink || newLink === item.link) return;
                              try {
                                await api.delete(`/bento-categories/${item.order}`);
                                await api.post('/bento-categories', { label:item.label, image:item.image, link:newLink.trim(), grid_position:slot.id });
                                toast.success('Link updated!'); fetchAll();
                              } catch (e) { toast.error('Error updating link'); }
                            }}
                            className="flex-shrink-0 flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 font-medium transition-colors">
                            <Pencil className="w-2.5 h-2.5" /> Edit
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="h-14 flex items-center justify-center text-gray-400 text-xs italic">Empty — click Add to fill this slot</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ SHOWCASE TAB ════════════════════════════════════════════════════ */}
        {tab === 'showcase' && (
          <div className="bg-white rounded-lg border p-6">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h2 className="text-lg font-bold">Customer Showcase</h2>
                <p className="text-xs text-gray-500 mt-1">Each card has 2 images — front shown by default, back revealed on hover.</p>
              </div>
              <button onClick={openAddModal} className="btn-primary flex items-center space-x-2 py-2 px-4">
                <Plus className="w-4 h-4" /><span>Add Card</span>
              </button>
            </div>
            {showcase.length === 0 ? (
              <div className="text-center py-16 text-gray-400 text-sm italic border-2 border-dashed rounded-lg">No cards yet.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {showcase.map((card, idx) => {
                  const imgs = Array.isArray(card.images) ? card.images : [card.image1, card.image2].filter(Boolean);
                  return (
                    <div key={card.order} className="border rounded-lg overflow-hidden">
                      <div className="grid grid-cols-2 gap-0.5 bg-gray-200">
                        {[0,1,2,3].map(i => (
                          <div key={i} className="relative bg-gray-100" style={{ aspectRatio:'1' }}>
                            {imgs[i] ? (
                              <>
                                <img src={imgs[i]} alt={`img ${i+1}`} className="w-full h-full object-cover object-top" onError={e => { e.target.style.display='none'; }} />
                                <span className="absolute top-1 left-1 text-[8px] bg-black/60 text-white px-1 py-0.5 rounded font-medium">{i+1}</span>
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[9px] text-gray-400">empty</div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="p-2 flex items-center justify-between bg-gray-50 border-t gap-1">
                        <span className="text-[10px] font-semibold text-gray-400">Card #{idx+1}</span>
                        <button onClick={() => openEditModal(card)} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-blue-300 text-blue-500 hover:bg-blue-50 font-medium"><Pencil className="w-2.5 h-2.5" /> Edit</button>
                        <button onClick={() => deleteShowcaseCard(card.order)} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-red-300 text-red-500 hover:bg-red-50 font-medium"><Trash2 className="w-2.5 h-2.5" /> Remove</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══ Hero Slide Modal ═════════════════════════════════════════════════ */}
      {heroModal && (
        <HeroSlideModal
          mode={heroModal.mode}
          slide={heroModal.slide}
          onClose={() => setHeroModal(null)}
          onSaved={() => { setHeroModal(null); fetchAll(); }}
        />
      )}

      {/* ══ Reel Modal ══════════════════════════════════════════════════════ */}
      {reelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background:'rgba(0,0,0,0.55)' }}
          onClick={e => { if (e.target === e.currentTarget) closeReelModal(); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background:'#2A0000' }}>
              <div>
                <h3 className="font-bold text-white text-sm">{reelModal.mode === 'add' ? 'Add YouTube Reel' : 'Edit YouTube Reel'}</h3>
                <p className="text-white/60 text-xs mt-0.5">Videos play automatically & loop on the storefront</p>
              </div>
              <button onClick={closeReelModal} className="text-white/70 hover:text-white text-lg leading-none">✕</button>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">YouTube Video URL <span className="text-red-500">*</span></label>
                <input type="text" value={reelForm.youtube_url}
                  onChange={e => setReelForm(f => ({ ...f, youtube_url: e.target.value }))}
                  placeholder="https://youtu.be/xxxxx"
                  autoFocus
                  className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30 font-mono"
                />
              </div>
              {previewVideoId ? (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-gray-700">Preview</p>
                  <div className="flex gap-3 items-start">
                    <img src={getYouTubeThumbnail(previewVideoId)} alt="thumbnail" className="w-24 h-14 object-cover rounded-lg border flex-shrink-0" />
                    <div className="space-y-1 min-w-0">
                      <p className="text-[10px] text-green-600 font-bold">✓ Valid YouTube URL detected</p>
                      <p className="text-[10px] text-gray-500 font-mono break-all">Video ID: {previewVideoId}</p>
                    </div>
                  </div>
                  <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio:'9/16', maxHeight:260 }}>
                    <iframe key={previewVideoId} src={buildEmbedUrl(previewVideoId)} title="Preview"
                      allow="autoplay; encrypted-media" className="absolute inset-0 w-full h-full border-0" />
                  </div>
                </div>
              ) : reelForm.youtube_url.trim() ? (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <p className="text-xs text-red-600 font-medium">⚠ Could not detect a valid YouTube video ID.</p>
                </div>
              ) : null}
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1.5">Caption <span className="text-gray-400 font-normal">(optional)</span></label>
                <input type="text" value={reelForm.caption}
                  onChange={e => setReelForm(f => ({ ...f, caption: e.target.value }))}
                  placeholder="e.g. New Bridal Collection 2026" maxLength={80}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={closeReelModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 font-medium">Cancel</button>
              <button onClick={submitReelModal} disabled={!previewVideoId}
                className="px-5 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-40"
                style={{ background:'#2A0000' }}>
                {reelModal.mode === 'add' ? 'Add Reel' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Showcase Modal ══════════════════════════════════════════════════ */}
      {scModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background:'rgba(0,0,0,0.5)' }}
          onClick={e => { if (e.target === e.currentTarget) closeScModal(); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ background:'#2A0000' }}>
              <h3 className="font-bold text-white text-sm">{scModal.mode === 'add' ? 'Add Customer Card' : 'Edit Customer Card'}</h3>
              <button onClick={closeScModal} className="text-white/70 hover:text-white text-lg leading-none">✕</button>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Add 1–4 images. On hover the card cycles through them. Image 1 is required.
              </p>
              {[0,1,2,3].map(i => (
                <div key={i}>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Image {i+1} {i===0 && <span className="text-red-500">*</span>}
                    {i>0 && <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span>}
                  </label>
                  <input type="text" value={scForm.images[i]}
                    onChange={e => { const u=[...scForm.images]; u[i]=e.target.value; setScForm(f=>({...f,images:u})); }}
                    placeholder={i===0?'https://... (required)':'https://... (optional)'}
                    autoFocus={i===0}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A0000]/30"
                  />
                  {scForm.images[i] && (
                    <img src={scForm.images[i]} alt={`preview ${i+1}`}
                      className="mt-2 w-full h-28 object-cover object-top rounded-lg border"
                      onError={e=>{e.target.style.display='none';}} onLoad={e=>{e.target.style.display='block';}} />
                  )}
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={closeScModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 font-medium">Cancel</button>
              <button onClick={submitScModal} disabled={!scForm.images[0]?.trim()}
                className="px-4 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-40"
                style={{ background:'#2A0000' }}>
                {scModal.mode==='add'?'Add Card':'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentPage;